import React, { useState, useEffect, useCallback, useRef } from "react";
import { callBackend } from "./utils/api";
import SplashScreen from "./components/SplashScreen";
import TrapTycoonEngine from "./components/TrapTycoonEngine";
import Auth from "./components/Auth";
import LandingPage from "./components/LandingPage";
import BanAppeal from "./components/BanAppeal";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import Footer from "./components/Footer";
import { useNotify } from "./components/Notification";
import { io } from "socket.io-client";
import { log } from "./utils/logger";

const socket = io({ path: "/socket.io" });

const clearWelcomeBackState = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith("welcomeBackShown:"))
    .forEach((key) => sessionStorage.removeItem(key));
};

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [stats, setStats] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loadingError, setLoadingError] = useState(null);
  const [version, setVersion] = useState("v0.0.0");
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isLoaded, setIsLoaded] = useState(false);
  const { addNotification } = useNotify();
  const [showLanding, setShowLanding] = useState(true);
  const [isSteamClient, setIsSteamClient] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(isSteamClient);
  const [returnSequence, setReturnSequence] = useState(0);
  const [returnIdleSeconds, setReturnIdleSeconds] = useState(0);
  const presenceQueue = useRef(Promise.resolve());
  const freshLogin = useRef(false);
  const presenceClientId = useRef(
    sessionStorage.getItem("trapPresenceClientId") ||
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    if (token && freshLogin.current) {
      clearWelcomeBackState();
      setReturnIdleSeconds(0);
      setReturnSequence((sequence) => sequence + 1);
      freshLogin.current = false;
    } else if (!token) {
      setReturnIdleSeconds(0);
      setReturnSequence(0);
    }
  }, [token]);

  useEffect(() => {
    sessionStorage.setItem("trapPresenceClientId", presenceClientId.current);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await callBackend("/auth/logout", { method: "POST" });
    } catch (err) {
      log("APP", "Server logout failed or session already expired.");
    } finally {
      clearWelcomeBackState();
      localStorage.removeItem("token");
      setToken(null);
      setStats(null);
    }
  }, []);

  // Handler for successful login
  const handleLoginSuccess = (newToken) => {
    freshLogin.current = true;
    localStorage.setItem("token", newToken);
    setToken(newToken);
  };

  const handleSplashComplete = useCallback(() => setIsLoaded(true), []);

  // Handler for submitting a ban appeal
  const handleAppeal = async (message) => {
    try {
      await callBackend("/auth/appeal", {
        method: "POST",
        body: { message },
      });
      addNotification("Wire sent to the top. Keep your pager on.", "success");
      setLoadingError("Appeal submitted.");
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const fetchStats = useCallback(async () => {
    // Prevent calls during the Steam handshake process
    if (!token || isAuthenticating || document.hidden) return;
    try {
      const data = await callBackend("/profile/me");

      // Use functional update to check level-up without depending on 'stats'
      setStats((prevStats) => {
        if (prevStats && data.level > prevStats.level) {
          addNotification(
            `WORD ON THE STREET: Level ${data.level} Kingpin.`,
            "success",
            "level_up",
          );
        }
        return data;
      });

      setLoadingError(null);
    } catch (err) {
      const criticalErrors = [
        "User not found.",
        "User vanished.",
        "Invalid credentials.",
        "Session expired or account reset. Please re-login.",
      ];
      if (criticalErrors.includes(err.message)) {
        handleLogout();
      } else {
        log("APP", "Sync interrupted.");
      }
    }
  }, [token, isAuthenticating, handleLogout, addNotification]);

  useEffect(() => {
    if (!token || isAuthenticating) return undefined;

    const queuePresenceUpdate = (endpoint, options) => {
      const request = presenceQueue.current
        .catch(() => undefined)
        .then(() => callBackend(endpoint, options));
      presenceQueue.current = request;
      return request;
    };

    const sleepPlayer = () => {
      clearWelcomeBackState();

      queuePresenceUpdate("/presence/sleep", {
        method: "POST",
        body: { clientId: presenceClientId.current },
        keepalive: true,
      }).catch((error) => {
        log("PRESENCE", `Could not mark player as sleeping: ${error.message}`);
      });
    };

    const wakePlayer = (showReturnReport) => {
      queuePresenceUpdate("/presence/wake", {
        method: "POST",
        body: { clientId: presenceClientId.current },
      })
        .then((presence) => {
          if (showReturnReport && presence.wasIdle) {
            clearWelcomeBackState();
            setReturnIdleSeconds(presence.sleptForSeconds);
            setReturnSequence((sequence) => sequence + 1);
          }
          if (showReturnReport) fetchStats();
        })
        .catch((error) => {
          log("PRESENCE", `Could not wake player: ${error.message}`);
          if (showReturnReport) fetchStats();
        });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        sleepPlayer();
      } else {
        wakePlayer(true);
      }
    };

    if (document.hidden) {
      sleepPlayer();
    } else {
      wakePlayer(true);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", sleepPlayer);
    const heartbeat = setInterval(() => {
      if (!document.hidden) wakePlayer(false);
    }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", sleepPlayer);
      clearInterval(heartbeat);
    };
  }, [token, isAuthenticating, fetchStats]);

  useEffect(() => {
    fetch(`/version.txt?t=${Date.now()}`, {
      cache: "no-store",
    })
      .then((res) => res.text())
      .then((text) => setVersion(text.trim()))
      .catch(() => setVersion("v0.0.0"));

    const fetchMaintenance = async () => {
      try {
        const data = await callBackend("/system/constants");
        setMaintenanceMode(data.ENGINE.MAINTENANCE_MODE);
      } catch (e) {
        log("APP", `Failed to fetch maintenance mode: ${e.message}`);
      }
    };
    fetchMaintenance();
    const interval = setInterval(fetchMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Trap Player Data Effect
  useEffect(() => {
    if (token) {
      // Fetch player stats immediately
      fetchStats();
      // Refresh player stats every 60 seconds
      const interval = setInterval(fetchStats, 60000);
      return () => clearInterval(interval);
    }
  }, [token, fetchStats]);

  // Refresh immediately after the server finishes processing an engine tick.
  useEffect(() => {
    if (!token) return undefined;

    const handleGlobalTick = () => {
      if (!document.hidden) fetchStats();
    };

    socket.on("global_tick", handleGlobalTick);
    return () => socket.off("global_tick", handleGlobalTick);
  }, [token, fetchStats]);

  // Trap Navigation Effect
  useEffect(() => {
    const handleLocationChange = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  // Navigation helper
  const navigateTo = (path) => {
    window.history.pushState({}, "", path);
    setCurrentPath(path);
  };

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const isSteam = userAgent.includes("TrapTycoon-Steam-Client");
    setIsSteamClient(isSteam);

    if (isSteam) {
      setShowLanding(false); // Hide landing immediately for Steam users
      handleSteamSilentLogin();
    }
  }, []);

  const handleSteamSilentLogin = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const steamid = urlParams.get("steamid");
    const ticket = urlParams.get("ticket");

    if (steamid && ticket) {
      setIsAuthenticating(true); // Lock API calls
      try {
        const response = await callBackend("/auth/steam/login", {
          method: "POST",
          body: { steamid, ticket },
        });

        if (response && response.token) {
          freshLogin.current = true;
          localStorage.setItem("token", response.token);
          setToken(response.token);
          setIsAuthenticating(false); // Unlock API calls
        }
      } catch (err) {
        setIsAuthenticating(false);
        setShowLanding(true);
      }
    }
  };

  //----------------------------------------------------------------------------
  // Extrenal Routing
  //----------------------------------------------------------------------------
  if (currentPath === "/terms") return <Terms onBack={() => navigateTo("/")} />;
  if (currentPath === "/privacy")
    return <Privacy onBack={() => navigateTo("/")} />;
  if (currentPath === "/appeal" && loadingError?.includes("restricted"))
    return (
      <BanAppeal
        onAppeal={handleAppeal}
        isSubmitted={loadingError.includes("Appeal submitted")}
      />
    );

  //----------------------------------------------------------------------------
  // Maintenance Mode Screen
  //----------------------------------------------------------------------------
  if (maintenanceMode && stats?.role !== "dev") {
    return (
      <div className="min-h-screen bg-black flex flex-col font-mono">
        {/* Wrapper to force content to center center */}
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="text-purple-500 uppercase font-black text-center animate-pulse">
            <div className="text-4xl mb-2">UNDER MAINTENANCE</div>
            <div className="text-zinc-500 text-xs tracking-[0.3em]">
              THE STREETS ARE CURRENTLY CLOSED
            </div>
          </div>
        </div>
        <Footer stats={stats} navigateTo={navigateTo} version={version} />
      </div>
    );
  }

  //----------------------------------------------------------------------------
  // Loading Error Screen (Banned, Restricted, or Connection Issues)
  //----------------------------------------------------------------------------
  if (loadingError) {
    return (
      <div className="min-h-screen bg-black flex flex-col font-mono">
        <div className="flex-grow flex items-center justify-center p-4">
          {loadingError.includes("restricted") ||
          loadingError.includes("Appeal submitted") ? (
            <BanAppeal
              onAppeal={handleAppeal}
              isSubmitted={loadingError.includes("Appeal submitted")}
            />
          ) : (
            <div className="text-center border-2 border-red-900 bg-red-900/10 p-12 max-w-md">
              <p className="mb-4 uppercase font-black text-red-500 text-2xl italic">
                STREET COMMS DOWN
              </p>
              <p className="text-xs text-red-800 mb-8 font-bold">
                {loadingError.toUpperCase()}
              </p>
              <button
                onClick={fetchStats}
                className="w-full border-2 border-red-600 bg-red-600 text-black py-3 font-black hover:bg-transparent hover:text-red-500 transition-all"
              >
                RE-ESTABLISH CONNECTION
              </button>
            </div>
          )}
        </div>
        <Footer stats={stats} navigateTo={navigateTo} version={version} />
      </div>
    );
  }

  //----------------------------------------------------------------------------
  // Landing / Login / Register Screen
  //----------------------------------------------------------------------------
  if (!token) {
    // If we're on Steam and waiting for the silent login, show a clean loading screen
    if (isSteamClient && !showLanding) {
      return (
        <div className="min-h-screen bg-black flex flex-col font-mono">
          <div className="flex-grow flex items-center justify-center">
            <div className="text-red-600 animate-pulse font-black text-xl uppercase">
              Establishing Secure Connection...
            </div>
          </div>
          <Footer stats={stats} navigateTo={navigateTo} version={version} />
        </div>
      );
    }

    // Standard Browser Flow
    return (
      <div className="min-h-screen bg-black font-mono flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          {showLanding ? (
            <LandingPage onShowAuth={() => setShowLanding(false)} />
          ) : (
            <Auth onLoginSuccess={handleLoginSuccess} navigateTo={navigateTo} />
          )}
        </div>
        <Footer stats={stats} navigateTo={navigateTo} version={version} />
      </div>
    );
  }

  //----------------------------------------------------------------------------
  // Player Data Loading Screen
  //----------------------------------------------------------------------------
  if (!stats) {
    return (
      <div className="min-h-screen bg-black flex flex-col font-mono">
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="text-purple-500 text-center">
            <div>LOADING INTEL</div>
            <div className="mt-2 text-zinc-500 text-xs">
              RECEIVING DATA FROM THE STREETS
            </div>
          </div>
        </div>
        <Footer stats={stats} navigateTo={navigateTo} version={version} />
      </div>
    );
  }

  //----------------------------------------------------------------------------
  // Main Game Screen
  //----------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-black text-green-400 font-mono flex flex-col">
      {maintenanceMode && stats?.role === "dev" && (
        <div className="bg-purple-600 text-white text-[10px] font-black py-1 px-4 text-center uppercase tracking-widest animate-pulse z-[9999]">
          ⚠️ MAINTENANCE MODE ACTIVE - ONLY DEVELOPERS CAN ACCESS THE BLOCKS
        </div>
      )}

      <div className="flex-grow">
        {!isLoaded && <SplashScreen onComplete={handleSplashComplete} />}
        <div
          className={
            isLoaded
              ? "opacity-100 transition-opacity duration-1000"
              : "opacity-0"
          }
        >
          <TrapTycoonEngine
            stats={stats}
            socket={socket}
            fetchStats={fetchStats}
            returnSequence={returnSequence}
            returnIdleSeconds={returnIdleSeconds}
            onLogout={handleLogout}
            navigateTo={navigateTo}
          />
        </div>
      </div>
      <Footer stats={stats} navigateTo={navigateTo} version={version} />
    </div>
  );
}
export default App;
